import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
  FiArrowRight,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiEye,
  FiEyeOff,
  FiEdit2,
  FiInfo,
  FiLock,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiUsers,
  FiX,
} from "react-icons/fi";
import type { PayoutAccount } from "./payoutData";
import { ensurePayoutsAccessToken, isPayoutApiMode, payoutApi } from "./api/payoutApiClient";
import { accountFromApiRecord, apiString } from "./api/payoutApiAdapters";
import { usePayoutResource } from "./api/usePayoutResource";
import {
  compactBody,
  errorMessage,
  isPayoutMockMode,
  isPayoutStepUpEnabled,
  stableIdempotencyKey,
  verifyStepUp,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import {
  digitsOnly,
  isValidEmail,
  validateOptionalEmail,
  validateOptionalIndianMobile,
} from "./api/payoutValidation";
import { GET_ALL_MEMBERS, GET_USER } from "../../../Qurries";
import { dashboardContext } from "../Dashboard";
import { showPayoutNotification } from "./PayoutNotification";

const selectOptionsByLabel: Record<string, string[]> = {
  Gender: ["Male", "Female", "Other", "Prefer not to say"],
  Department: ["Finance", "Accounts", "Operations", "Administration", "Compliance"],
  "Manager (Reporting To)": [],
  "Employment Type": ["Full Time", "Part Time", "Contract", "Consultant", "Intern"],
  "Work Location": ["Mumbai", "Delhi", "Bengaluru", "Hyderabad", "Remote"],
  "User Status": ["Active", "Inactive", "Suspended"],
  State: ["Maharashtra", "Delhi", "Karnataka", "Telangana", "Tamil Nadu", "Uttar Pradesh"],
  Country: ["India"],
  "ID Proof Type": ["Aadhaar", "PAN", "Passport", "Voter ID", "Driving License"],
  "Blood Group (Optional)": ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"],
};

const countryCodeOptions = [
  { label: "IN +91", value: "+91" },
  { label: "US +1", value: "+1" },
  { label: "AE +971", value: "+971" },
  { label: "GB +44", value: "+44" },
  { label: "SG +65", value: "+65" },
];

const addUserRoles = [
  {
    level: "Level 2",
    title: "Initiator",
    description: "Can initiate transfers, add and manage beneficiaries and view reports.",
    icon: <FiUsers />,
    tone: "bg-[#6687FF1A] text-[#1B163B]",
    can: [
      "View dashboards and reports",
      "Initiate and save transfers",
      "Add and manage beneficiaries",
      "View pending approvals",
      "Download statements",
    ],
    cannot: [
      "Approve or reject transfers",
      "Manage users and roles",
      "Access approval workflows and settings",
    ],
  },
  {
    level: "Level 1",
    title: "Viewer",
    description: "Read only access to view dashboards, reports, beneficiaries and statements.",
    icon: <FiEye />,
    tone: "bg-[#16A34A1A] text-[#166534]",
    can: [
      "View dashboards and reports",
      "View beneficiaries",
      "Download statements",
    ],
    cannot: [
      "Initiate transfers",
      "Approve or reject transfers",
      "Manage users and roles",
    ],
  },
  {
    level: "Level 3",
    title: "Manager",
    description: "All Level 1 & Level 2 access + manage users and assign roles.",
    icon: <FiShield />,
    tone: "bg-[#A9B2CF1A] text-[#1B163B]",
    can: [
      "Manage users and roles",
      "Initiate and save transfers",
      "View pending approvals",
      "Download statements",
    ],
    cannot: [
      "Change approval workflow matrix",
      "Override approval limits",
    ],
  },
  {
    level: "Level 4",
    title: "Administrator",
    description: "Full access including approval workflows, permission management and settings.",
    icon: <FiLock />,
    tone: "bg-[#F59E0B1A] text-[#92400E]",
    can: [
      "Manage all payout users",
      "Configure roles and permissions",
      "Access approval workflows and settings",
      "Download statements and reports",
    ],
    cannot: ["-"],
  },
];

type AddUserAccount = PayoutAccount & {
  type: string;
  selectedByDefault?: boolean;
};

type EditablePayoutUser = {
  id?: string;
  initials?: string;
  name: string;
  role: string;
  accessLevel?: string;
  email: string;
  mobile: string;
  status: string;
  payoutAccountIds: string[];
  accountsAccess?: string;
  employeeId?: string;
  dob?: string;
  gender?: string;
  personalEmail?: string;
  alternateMobile?: string;
  department?: string;
  designation?: string;
  joiningDate?: string;
  manager?: string;
  employmentType?: string;
  workLocation?: string;
  address?: {
    line1?: string;
    city?: string;
    state?: string;
    pinCode?: string;
    country?: string;
    landmark?: string;
  };
  idProofType?: string;
  idProofNumber?: string;
  bloodGroup?: string;
  emergencyContact?: {
    name?: string;
    mobile?: string;
    relationship?: string;
  };
};

const addUserAccountOptions: Array<{
  group: string;
  accounts: AddUserAccount[];
}> = [];

const flatAddUserAccounts = addUserAccountOptions.flatMap((group) => group.accounts);
const defaultSelectedAccountIds = flatAddUserAccounts
  .filter((account) => account.selectedByDefault)
  .map((account) => account.id);

const emptyAddUserAccount: AddUserAccount = {
  id: "",
  name: "",
  school: "",
  bank: "",
  logoUrl: "",
  accountNumber: "",
  ifsc: "",
  currency: "INR",
  addedOn: "",
  availableBalance: 0,
  accountBalance: 0,
  fundsOnHold: 0,
  overdraftBalance: 0,
  overdraftLimit: 0,
  lowBalanceThreshold: 0,
  accent: "bg-[#6687FF]",
  type: "",
};

function FieldLabel({
  children,
  required = false,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <span className="mb-2 block text-xs font-semibold text-[#505E8E]">
      {children}
      {required && <span className="text-[#EF4444]"> *</span>}
    </span>
  );
}

function TextInput({
  label,
  placeholder,
  required = false,
  rightIcon,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  required?: boolean;
  rightIcon?: React.ReactNode;
  value?: string;
  onChange?: (value: string) => void;
}) {
  const [internalValue, setInternalValue] = React.useState("");
  const resolvedValue = value ?? internalValue;
  return (
    <label className="block">
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-3">
        <input
          value={resolvedValue}
          onChange={(event) => {
            setInternalValue(event.target.value);
            onChange?.(event.target.value);
          }}
          className="w-full bg-transparent text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
          placeholder={placeholder}
        />
        {rightIcon && <span className="ml-2 shrink-0 text-[#505E8E]">{rightIcon}</span>}
      </div>
    </label>
  );
}

type MerchantEmailOption = {
  email: string;
  name: string;
  phone?: string;
  source: "owner" | "member";
};

function MerchantEmailSelect({
  label,
  required = false,
  value,
  options,
  disabled = false,
  onSelect,
}: {
  label: string;
  required?: boolean;
  value: string;
  options: MerchantEmailOption[];
  disabled?: boolean;
  onSelect: (option: MerchantEmailOption) => void;
}) {
  return (
    <label className="block">
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="relative">
        <select
          disabled={disabled}
          value={value}
          onChange={(event) => {
            const email = event.target.value;
            const match = options.find((item) => item.email === email);
            if (match) onSelect(match);
          }}
          className="h-10 w-full appearance-none rounded-[6px] border border-[#E0E5F2] bg-white px-3 pr-9 text-sm font-medium text-[#1B163B] outline-none disabled:bg-[#F8FAFC]"
        >
          <option value="">Select merchant dashboard email</option>
          {options.map((option) => (
            <option key={option.email} value={option.email}>
              {option.email}
              {option.name ? ` — ${option.name}` : ""}
            </option>
          ))}
        </select>
        <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#505E8E]" />
      </div>
      {!options.length && (
        <p className="mt-2 text-xs font-medium text-[#505E8E]">
          No eligible merchant emails left to add for this school.
        </p>
      )}
    </label>
  );
}

function formatDateInput(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function DateInput({
  label,
  required = false,
  value,
  onChange,
}: {
  label: string;
  required?: boolean;
  value?: string;
  onChange?: (value: string) => void;
}) {
  const [internalValue, setInternalValue] = React.useState("");
  const resolvedValue = value ?? internalValue;

  return (
    <label className="block">
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-3">
        <input
          value={resolvedValue}
          inputMode="numeric"
          maxLength={10}
          onChange={(event) => {
            const nextValue = formatDateInput(event.target.value);
            setInternalValue(nextValue);
            onChange?.(nextValue);
          }}
          className="w-full bg-transparent text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
          placeholder="DD/MM/YYYY"
        />
        <FiCalendar className="ml-2 shrink-0 text-[#505E8E]" />
      </div>
    </label>
  );
}

function SelectInput({
  label,
  value,
  required = false,
  options,
  className = "",
  onChange,
}: {
  label: string;
  value: string;
  required?: boolean;
  options?: string[];
  className?: string;
  onChange?: (value: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(value);
  const resolvedOptions = options ?? selectOptionsByLabel[label] ?? [value];
  const isPlaceholder = selected.startsWith("Select");
  const statusDotClassName: Record<string, string> = {
    Active: "bg-[#16A34A]",
    Inactive: "bg-[#A3AED0]",
    Suspended: "bg-[#EF4444]",
  };
  const shouldShowStatusDot = label === "User Status";

  React.useEffect(() => {
    setSelected(value);
  }, [value]);

  return (
    <label className={"relative block " + className}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex h-10 w-full items-center justify-between rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-left text-sm font-semibold text-[#1B163B]"
      >
        <span className={isPlaceholder ? "truncate text-[#A3AED0]" : "truncate"}>
          {selected}
        </span>
        <FiChevronDown
          className={
            "ml-2 shrink-0 text-[#505E8E] transition-transform " +
            (open ? "rotate-180" : "")
          }
        />
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-[100] mt-1 max-h-56 overflow-y-auto rounded-[8px] border border-[#E0E5F2] bg-white py-1 shadow-[0_14px_28px_rgba(27,22,59,0.12)]">
          {resolvedOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                setSelected(option);
                setOpen(false);
                onChange?.(option);
              }}
              className={
                "flex w-full items-center justify-between px-3 py-2.5 text-left text-sm font-medium " +
                (option === selected
                  ? "bg-[#A9B2CF1A] text-[#1B163B]"
                  : "text-[#1B163B] hover:bg-[#A9B2CF1A]")
              }
            >
              <span>{option}</span>
              {shouldShowStatusDot ? (
                <span
                  className={
                    "h-2 w-2 rounded-full " +
                    (statusDotClassName[option] ?? "bg-[#A3AED0]")
                  }
                />
              ) : (
                option === selected && <span className="h-2 w-2 rounded-full bg-[#1B163B]" />
              )}
            </button>
          ))}
        </div>
      )}
    </label>
  );
}

function PhoneInput({
  label,
  placeholder,
  required = false,
  className = "",
  code,
  value,
  onCodeChange,
  onChange,
}: {
  label: string;
  placeholder: string;
  required?: boolean;
  className?: string;
  code?: string;
  value?: string;
  onCodeChange?: (value: string) => void;
  onChange?: (value: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [selectedCode, setSelectedCode] = React.useState(
    countryCodeOptions.find((option) => option.value === code) || countryCodeOptions[0],
  );
  const [internalValue, setInternalValue] = React.useState("");
  const resolvedValue = value ?? internalValue;

  React.useEffect(() => {
    if (!code) return;
    const nextCode = countryCodeOptions.find((option) => option.value === code);
    if (nextCode) setSelectedCode(nextCode);
  }, [code]);

  return (
    <label className={"block " + className}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="grid grid-cols-[72px_minmax(0,1fr)] gap-2">
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((current) => !current)}
            className="flex h-10 w-full items-center justify-between rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-semibold text-[#1B163B]"
          >
            {selectedCode.value}
            <FiChevronDown
              className={
                "text-[#505E8E] transition-transform " + (open ? "rotate-180" : "")
              }
            />
          </button>
          {open && (
            <div className="absolute left-0 z-[100] mt-1 w-28 overflow-hidden rounded-[8px] border border-[#E0E5F2] bg-white py-1 shadow-[0_14px_28px_rgba(27,22,59,0.12)]">
              {countryCodeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setSelectedCode(option);
                    onCodeChange?.(option.value);
                    setOpen(false);
                  }}
                  className={
                    "block w-full px-3 py-2 text-left text-sm font-medium " +
                    (option.value === selectedCode.value
                      ? "bg-[#A9B2CF1A] text-[#1B163B]"
                      : "text-[#1B163B] hover:bg-[#A9B2CF1A]")
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          value={resolvedValue}
          onChange={(event) => {
            const nextValue = event.target.value.replace(/\D/g, "").slice(0, 10);
            setInternalValue(nextValue);
            onChange?.(nextValue);
          }}
          className="h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
          placeholder={placeholder}
        />
      </div>
    </label>
  );
}

function TextAreaInput({
  label,
  placeholder,
  required = false,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  required?: boolean;
  value?: string;
  onChange?: (value: string) => void;
}) {
  const [internalValue, setInternalValue] = React.useState("");
  const resolvedValue = value ?? internalValue;
  return (
    <label className="block">
      <FieldLabel required={required}>{label}</FieldLabel>
      <textarea
        value={resolvedValue}
        onChange={(event) => {
          const nextValue = event.target.value.slice(0, 200);
          setInternalValue(nextValue);
          onChange?.(nextValue);
        }}
        className="min-h-[78px] w-full resize-none rounded-[6px] border border-[#E0E5F2] bg-white px-3 py-3 text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
        placeholder={placeholder}
      />
      <span className="mt-1 block text-right text-xs font-medium text-[#505E8E]">
        {resolvedValue.length} / 200
      </span>
    </label>
  );
}

function PasswordInput({
  label,
  placeholder,
  required = false,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  required?: boolean;
  value?: string;
  onChange?: (value: string) => void;
}) {
  const [visible, setVisible] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState("");
  const resolvedValue = value ?? internalValue;

  return (
    <label className="block">
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-3">
        <input
          type={visible ? "text" : "password"}
          value={resolvedValue}
          onChange={(event) => {
            setInternalValue(event.target.value);
            onChange?.(event.target.value);
          }}
          className="w-full bg-transparent text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          className="ml-2 shrink-0 text-[#505E8E]"
          aria-label={visible ? "Hide password" : "Show password"}
        >
          {visible ? <FiEyeOff /> : <FiEye />}
        </button>
      </div>
    </label>
  );
}

function StepItem({
  number,
  label,
  active = false,
  complete = false,
  onClick,
}: {
  number: number;
  label: string;
  active?: boolean;
  complete?: boolean;
  onClick?: () => void;
}) {
  const isClickable = Boolean(complete && onClick);

  return (
    <button
      type="button"
      onClick={isClickable ? onClick : undefined}
      disabled={!isClickable}
      className={
        "relative flex min-h-[46px] items-center gap-3 px-4 text-left transition " +
        (isClickable ? "cursor-pointer hover:bg-[#F4F7FE]" : "cursor-default")
      }
      aria-label={isClickable ? `Go back to ${label}` : label}
    >
      <span
        className={
          "flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold " +
          (active
            ? "bg-[#1B163B] text-white"
            : complete
            ? "bg-[#A9B2CF1A] text-[#1B163B]"
            : "bg-[#F4F7FE] text-[#1B163B]")
        }
      >
        {complete ? <FiCheck className="text-sm" /> : number}
      </span>
      <span className="text-sm font-semibold text-[#1B163B]">{label}</span>
      {active && (
        <span className="absolute bottom-[-1px] left-4 h-[2px] w-[170px] rounded-full bg-[#1B163B]" />
      )}
    </button>
  );
}

function InfoCallout({
  icon,
  children,
  tone = "blue",
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  tone?: "blue" | "green";
}) {
  return (
    <div
      className={
        "flex gap-3 rounded-[6px] px-4 py-3 text-sm font-medium leading-5 " +
        (tone === "green"
          ? "bg-[#16A34A1A] text-[#1B163B]"
          : "bg-[#A9B2CF1A] text-[#505E8E]")
      }
    >
      <span
        className={
          "mt-0.5 shrink-0 " + (tone === "green" ? "text-[#16A34A]" : "text-[#1B163B]")
        }
      >
        {icon}
      </span>
      {children}
    </div>
  );
}

function UserSummary({
  selectedRole,
  selectedAccountCount = 0,
  name = "New User",
  initials = "-",
  showAccountCount = false,
}: {
  selectedRole?: (typeof addUserRoles)[number];
  selectedAccountCount?: number;
  name?: string;
  initials?: string;
  showAccountCount?: boolean;
}) {
  return (
    <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
      <h2 className="text-[16px] font-semibold text-[#1B163B]">
        User Summary
      </h2>
      <div className="my-5 h-px bg-[#E0E5F2]" />
      <div className="mb-5 flex items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F4F7FE] text-lg font-semibold text-[#505E8E]">
          {initials}
        </div>
        <div>
          <p className="text-sm font-semibold text-[#1B163B]">{name}</p>
          <span className="mt-2 inline-flex rounded-[5px] bg-[#A9B2CF1A] px-2 py-1 text-xs font-semibold text-[#505E8E]">
            {selectedRole?.level ?? "-"}
          </span>
        </div>
      </div>

      <div className="space-y-4 text-sm">
        {[
          ["Role", selectedRole?.title ?? "-"],
          ["Access Level", selectedRole?.level ?? "-"],
          ["Accounts", showAccountCount ? `${selectedAccountCount} selected` : "-"],
        ].map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4">
            <span className="font-medium text-[#505E8E]">{label}</span>
            <span className="font-semibold text-[#1B163B]">{value}</span>
          </div>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        <InfoCallout icon={<FiInfo />}>
          The user will receive an invite after completing all the steps.
        </InfoCallout>
        <InfoCallout icon={<FiShield />} tone="green">
          Access is controlled by predefined roles and inherited permissions.
        </InfoCallout>
      </div>
    </aside>
  );
}

function AccountStatusBadge() {
  return (
    <span className="rounded-[5px] bg-[#E7F6EC] px-3 py-1 text-xs font-semibold text-[#2E7D32]">
      Active
    </span>
  );
}

function CurrencyBadge({ currency }: { currency: string }) {
  return (
    <span className="rounded-[5px] border border-[#E0E5F2] bg-[#F8FAFD] px-3 py-1 text-xs font-semibold text-[#505E8E]">
      {currency}
    </span>
  );
}

function AccountGroup({
  group,
  selectedAccountIds,
  onToggleAccount,
  onToggleGroup,
}: {
  group: (typeof addUserAccountOptions)[number];
  selectedAccountIds: string[];
  onToggleAccount: (accountId: string) => void;
  onToggleGroup: (accountIds: string[]) => void;
}) {
  const groupAccountIds = group.accounts.map((account) => account.id);
  const isGroupSelected = groupAccountIds.every((id) => selectedAccountIds.includes(id));

  return (
    <div className="overflow-hidden rounded-[8px] border border-[#E0E5F2] bg-white">
      <button
        type="button"
        onClick={() => onToggleGroup(groupAccountIds)}
        className="flex h-12 w-full items-center justify-between border-b border-[#E0E5F2] bg-white px-4 text-left"
      >
        <span className="flex items-center gap-3">
          <span
            className={
              "flex h-4 w-4 items-center justify-center rounded-[3px] border " +
              (isGroupSelected ? "border-[#6687FF] bg-[#6687FF]" : "border-[#A3AED0] bg-white")
            }
          >
            {isGroupSelected && <FiCheck className="text-[10px] text-white" />}
          </span>
          <span className="text-sm font-semibold text-[#1B163B]">
            {group.group} ({group.accounts.length})
          </span>
        </span>
        <FiChevronDown className="text-[#505E8E]" />
      </button>

      {group.accounts.map((account) => {
        const isSelected = selectedAccountIds.includes(account.id);

        return (
          <label
            key={account.id}
            className="grid min-h-[74px] cursor-pointer grid-cols-[24px_minmax(0,1fr)_auto_auto] items-center gap-4 border-b border-[#E0E5F2] px-4 last:border-b-0"
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleAccount(account.id)}
              className="h-4 w-4 accent-[#6687FF]"
            />
            <span className="flex items-center gap-4">
              <img
                src={account.logoUrl}
                alt={`${account.bank} logo`}
                className="h-7 w-16 object-contain"
              />
              <span>
                <span className="block text-sm font-semibold text-[#1B163B]">
                  {account.name}
                </span>
                <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                  {account.bank} **** {account.accountNumber}
                </span>
                <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                  {account.type}
                </span>
              </span>
            </span>
            <AccountStatusBadge />
            <CurrencyBadge currency={account.currency} />
          </label>
        );
      })}
    </div>
  );
}

function ReviewEditButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-xs font-semibold text-[#6687FF]"
    >
      <FiEdit2 /> Edit
    </button>
  );
}

const defaultUserFormDraft = {
  fullName: "",
  employeeId: "",
  dob: "",
  gender: "Select gender",
  companyEmail: "",
  personalEmail: "",
  mobileCode: "+91",
  mobile: "",
  alternateMobileCode: "+91",
  alternateMobile: "",
  department: "Select department",
  designation: "",
  joiningDate: "",
  manager: "Select manager",
  employmentType: "Select employment type",
  workLocation: "Select work location",
  status: "Active",
  address: "",
  city: "",
  state: "Select state",
  pinCode: "",
  country: "India",
  landmark: "",
  idProofType: "Select ID type",
  idProofNumber: "",
  bloodGroup: "Select blood group",
  emergencyName: "",
  emergencyCode: "+91",
  emergencyMobile: "",
  relationship: "",
};

const roleFromUser = (user?: EditablePayoutUser) => {
  const raw = `${user?.accessLevel || ""} ${user?.role || ""}`.toLowerCase();
  if (raw.includes("administrator") || raw.includes("admin") || raw.includes("level 4")) {
    return addUserRoles[3];
  }
  if (raw.includes("manager") || raw.includes("checker") || raw.includes("level 3")) {
    return addUserRoles[2];
  }
  if (raw.includes("observer") || raw.includes("viewer") || raw.includes("level 1")) {
    return addUserRoles[1];
  }
  return addUserRoles[0];
};

const splitIndianMobile = (mobile: string) => {
  const digits = mobile.replace(/\D/g, "");
  if (digits.length > 10) {
    return { code: `+${digits.slice(0, digits.length - 10)}`, number: digits.slice(-10) };
  }
  return { code: "+91", number: digits };
};

const dateForUserForm = (value?: string) => {
  if (!value) return "";
  const trimmed = value.trim();
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}/${parsed.getFullYear()}`;
  }
  return trimmed;
};

const optionOrPlaceholder = (value: string | undefined, placeholder: string, options: string[]) =>
  value && options.includes(value) ? value : placeholder;

const draftFromUser = (user?: EditablePayoutUser) => {
  if (!user) return defaultUserFormDraft;
  const mobile = splitIndianMobile(user.mobile || "");
  const alternateMobile = splitIndianMobile(user.alternateMobile || "");
  const emergencyMobile = splitIndianMobile(user.emergencyContact?.mobile || "");
  return {
    ...defaultUserFormDraft,
    fullName: user.name || "",
    employeeId: user.employeeId || "",
    dob: dateForUserForm(user.dob),
    gender: optionOrPlaceholder(user.gender, defaultUserFormDraft.gender, selectOptionsByLabel.Gender),
    companyEmail: user.email || "",
    personalEmail: user.personalEmail || "",
    mobileCode: mobile.code,
    mobile: mobile.number,
    alternateMobileCode: alternateMobile.code,
    alternateMobile: alternateMobile.number,
    department: optionOrPlaceholder(user.department, defaultUserFormDraft.department, selectOptionsByLabel.Department),
    designation: user.designation || "",
    joiningDate: dateForUserForm(user.joiningDate),
    manager: optionOrPlaceholder(user.manager, defaultUserFormDraft.manager, selectOptionsByLabel["Manager (Reporting To)"]),
    employmentType: optionOrPlaceholder(user.employmentType, defaultUserFormDraft.employmentType, selectOptionsByLabel["Employment Type"]),
    workLocation: optionOrPlaceholder(user.workLocation, defaultUserFormDraft.workLocation, selectOptionsByLabel["Work Location"]),
    status: user.status || "Active",
    address: user.address?.line1 || "",
    city: user.address?.city || "",
    state: optionOrPlaceholder(user.address?.state, defaultUserFormDraft.state, selectOptionsByLabel.State),
    pinCode: user.address?.pinCode || "",
    country: optionOrPlaceholder(user.address?.country, defaultUserFormDraft.country, selectOptionsByLabel.Country),
    landmark: user.address?.landmark || "",
    idProofType: optionOrPlaceholder(user.idProofType, defaultUserFormDraft.idProofType, selectOptionsByLabel["ID Proof Type"]),
    idProofNumber: user.idProofNumber || "",
    bloodGroup: optionOrPlaceholder(user.bloodGroup, defaultUserFormDraft.bloodGroup, selectOptionsByLabel["Blood Group (Optional)"]),
    emergencyName: user.emergencyContact?.name || "",
    emergencyCode: emergencyMobile.code,
    emergencyMobile: emergencyMobile.number,
    relationship: user.emergencyContact?.relationship || "",
  };
};

function PayoutAddUser({
  mode = "create",
  initialUser,
}: {
  mode?: "create" | "edit";
  initialUser?: EditablePayoutUser;
}) {
  const navigate = useNavigate();
  const { user: dashboardUser } = React.useContext(dashboardContext) || {};
  const stepUpRequired = isPayoutStepUpEnabled();
  const isEditMode = mode === "edit";
  const [currentStep, setCurrentStep] = React.useState(1);
  const [apiRoleIdsByLevel, setApiRoleIdsByLevel] = React.useState<Record<string, string>>({});
  const [formDraft, setFormDraft] = React.useState(() => draftFromUser(initialUser));
  const [selectedRole, setSelectedRole] = React.useState(() => roleFromUser(initialUser));
  const [selectedAccountIds, setSelectedAccountIds] = React.useState<string[]>(
    initialUser?.payoutAccountIds?.length
      ? initialUser.payoutAccountIds
      : defaultSelectedAccountIds,
  );
  const [accountSearch, setAccountSearch] = React.useState("");
  const [verificationMethod] = React.useState<"authenticator">("authenticator");
  const [verificationCode, setVerificationCode] = React.useState<string[]>(
    Array(6).fill(""),
  );
  const [submitting, setSubmitting] = React.useState(false);
  const [, setSubmitError] = React.useState("");
  const showSubmitError = React.useCallback((message: string) => {
    setSubmitError(message);
    showPayoutNotification({ tone: "error", message });
  }, []);
  const [enrolledEmails, setEnrolledEmails] = React.useState<Set<string>>(new Set());

  const { data: merchantQueryData } = useQuery(GET_USER, {
    skip: isEditMode,
    errorPolicy: "ignore",
  });
  const { data: membersData } = useQuery(GET_ALL_MEMBERS, {
    skip: isEditMode,
    errorPolicy: "ignore",
  });

  const accountsResource = usePayoutResource<AddUserAccount[]>(
    [] as AddUserAccount[],
    (signal) =>
      payoutApi.accounts(signal).then((accounts) =>
        accounts.map((account) => ({
          ...accountFromApiRecord(account, emptyAddUserAccount),
          type: "Current Account",
          selectedByDefault: false,
        })),
      ),
  );
  const liveAddUserAccounts =
    isPayoutApiMode() && !accountsResource.usingFallback && accountsResource.data.length
      ? accountsResource.data
      : [];
  const liveAddUserAccountOptions =
    isPayoutApiMode() && !accountsResource.usingFallback && accountsResource.data.length
      ? [{ group: "Payout Accounts", accounts: liveAddUserAccounts }]
      : [];

  const eligibleEmailOptions = React.useMemo(() => {
    const merchant =
      merchantQueryData?.getMerchantQuery ||
      dashboardUser?.getMerchantQuery ||
      dashboardUser ||
      {};
    const options: MerchantEmailOption[] = [];
    const ownerEmail = String(merchant.email_id || merchant.email || "")
      .trim()
      .toLowerCase();
    if (ownerEmail) {
      options.push({
        email: ownerEmail,
        name: String(merchant.user || merchant.name || "Owner"),
        phone: merchant.phone_number ? String(merchant.phone_number) : undefined,
        source: "owner",
      });
    }
    const members = membersData?.getAllMerchantMembers || [];
    for (const member of members) {
      const email = String(member?.email || "")
        .trim()
        .toLowerCase();
      if (!email) continue;
      if (options.some((item) => item.email === email)) continue;
      options.push({
        email,
        name: String(member?.name || email),
        phone: member?.phone_number ? String(member.phone_number) : undefined,
        source: "member",
      });
    }
    return options.filter((item) => {
      if (isEditMode && item.email === String(initialUser?.email || "").toLowerCase()) {
        return true;
      }
      return !enrolledEmails.has(item.email);
    });
  }, [
    merchantQueryData,
    dashboardUser,
    membersData,
    enrolledEmails,
    isEditMode,
    initialUser?.email,
  ]);

  const managerNameOptions = React.useMemo(
    () =>
      eligibleEmailOptions
        .map((item) => item.name)
        .filter((name) => Boolean(name && name.includes("@") === false)),
    [eligibleEmailOptions],
  );

  React.useEffect(() => {
    if (!isPayoutApiMode()) return;
    ensurePayoutsAccessToken().catch(() => undefined);
  }, []);
  React.useEffect(() => {
    if (!isPayoutApiMode() || isEditMode) return;
    const controller = new AbortController();
    payoutApi
      .users({ limit: 200 }, controller.signal)
      .then((rows) => {
        const next = new Set<string>();
        rows.forEach((row) => {
          const email = apiString(row, ["email"], "").toLowerCase();
          if (email) next.add(email);
        });
        setEnrolledEmails(next);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [isEditMode]);
  React.useEffect(() => {
    if (!isPayoutApiMode()) return;
    const controller = new AbortController();
    const levelFromRoleRecord = (row: Record<string, unknown>) => {
      const direct = apiString(row, ["level_label", "levelLabel"], "");
      if (direct) return direct;
      const raw = apiString(row, ["access_level", "accessLevel", "name", "slug"], "")
        .toLowerCase()
        .replace(/[_-]/g, " ");
      if (raw.includes("administrator") || raw.includes("admin")) return "Level 4";
      if (raw.includes("finance manager") || raw.includes("checker")) return "Level 3";
      if (raw.includes("finance executive") || raw.includes("maker")) return "Level 2";
      if (raw.includes("observer") || raw.includes("viewer")) return "Level 1";
      return "";
    };
    payoutApi
      .roles({ limit: 20 }, controller.signal)
      .then((rows) => {
        const map: Record<string, string> = {};
        rows.forEach((row) => {
          const level = levelFromRoleRecord(row);
          const roleId = apiString(row, ["role_id", "roleId"], "");
          if (level && roleId) map[level] = roleId;
        });
        setApiRoleIdsByLevel(map);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);
  React.useEffect(() => {
    if (!isPayoutApiMode() || accountsResource.usingFallback || !accountsResource.data.length) {
      return;
    }
    if (!isEditMode) {
      setSelectedAccountIds([accountsResource.data[0].id]);
      return;
    }
    setSelectedAccountIds((current) => {
      if (current.length) return current;
      const accountNames = (initialUser?.accountsAccess || "")
        .split(",")
        .map((account) => account.trim().toLowerCase())
        .filter(Boolean);
      const matched = accountsResource.data
        .filter((account) => accountNames.includes(account.name.toLowerCase()))
        .map((account) => account.id);
      return matched;
    });
  }, [accountsResource.data, accountsResource.usingFallback, initialUser?.accountsAccess, isEditMode]);
  React.useEffect(() => {
    if (!initialUser) return;
    setFormDraft(draftFromUser(initialUser));
    setSelectedRole(roleFromUser(initialUser));
    setSelectedAccountIds(initialUser.payoutAccountIds || []);
  }, [initialUser]);
  const selectedAccounts = liveAddUserAccounts.filter((account) =>
    selectedAccountIds.includes(account.id),
  );
  const updateDraft = (field: keyof typeof formDraft) => (value: string) => {
    setFormDraft((current) => ({ ...current, [field]: value }));
  };
  const validatePersonalInfo = () => {
    if (!formDraft.fullName.trim()) return "Full name is required.";
    if (!isValidEmail(formDraft.companyEmail.trim())) {
      return "Select a merchant dashboard email.";
    }
    if (
      !isEditMode &&
      !eligibleEmailOptions.some(
        (item) => item.email === formDraft.companyEmail.trim().toLowerCase(),
      )
    ) {
      return "Select an email that already has merchant dashboard access.";
    }
    const personalEmailError = validateOptionalEmail(
      formDraft.personalEmail,
      "Enter a valid personal email.",
    );
    if (personalEmailError) return personalEmailError;
    const mobileError = validateOptionalIndianMobile(formDraft.mobile);
    if (mobileError) return mobileError;
    const alternateMobileError = validateOptionalIndianMobile(
      formDraft.alternateMobile,
      "Enter a valid alternate mobile number.",
    );
    if (alternateMobileError) return alternateMobileError;
    return "";
  };

  const hasContactAddress = Boolean(
    formDraft.address.trim() ||
    formDraft.city.trim() ||
    !formDraft.state.startsWith("Select") ||
    formDraft.pinCode.trim() ||
    formDraft.landmark.trim() ||
    (formDraft.country.trim() &&
      formDraft.country.trim() !== defaultUserFormDraft.country),
  );

  const handleSelectMerchantEmail = (option: MerchantEmailOption) => {
    setFormDraft((current) => ({
      ...current,
      companyEmail: option.email,
      fullName: current.fullName.trim() ? current.fullName : option.name,
      mobile:
        current.mobile.trim() ||
        (option.phone ? option.phone.replace(/\D/g, "").slice(-10) : ""),
    }));
  };

  const handleContinue = async () => {
    if (currentStep === 1) {
      const validationError = validatePersonalInfo();
      if (validationError) {
        showSubmitError(`Add user - Personal information: ${validationError}`);
        return;
      }
      setSubmitError("");
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2 && !selectedRole) {
      showSubmitError("Add user - Assign role: select a role before continuing.");
      return;
    }

    if (currentStep === 3 && selectedAccountIds.length === 0) {
      showSubmitError("Add user - Assign accounts: select at least one payout account before continuing.");
      return;
    }

    if (currentStep === 5) {
      const code = verificationCode.join("");
      if ((stepUpRequired && code.length !== 6) || submitting) return;
      setSubmitError("");

      if (isPayoutMockMode()) {
        showSubmitError("Add user verification: mock mode is disabled for user enrollment. Use API mode.");
        return;
      }

      const roleId = apiRoleIdsByLevel[selectedRole.level];
      if (!roleId) {
        showSubmitError("Add user - Assign role: unable to resolve the selected role. Refresh the page and try again.");
        return;
      }

      setSubmitting(true);
      try {
        await ensurePayoutsAccessToken();
        const stepUpToken = stepUpRequired
          ? await verifyStepUp({
              purpose: isEditMode ? "UPDATE_USER" : "CREATE_USER",
              method: verificationMethod,
              code,
            })
          : undefined;
        const body = compactBody({
          name: formDraft.fullName.trim(),
          email: formDraft.companyEmail.trim(),
          personal_email: formDraft.personalEmail.trim(),
          mobile: `${formDraft.mobileCode}${digitsOnly(formDraft.mobile)}`,
          alternate_mobile_number: formDraft.alternateMobile
            ? `${formDraft.alternateMobileCode}${digitsOnly(formDraft.alternateMobile)}`
            : undefined,
          employee_id: formDraft.employeeId.trim(),
          date_of_birth: formDraft.dob,
          gender: formDraft.gender.startsWith("Select") ? undefined : formDraft.gender,
          department: formDraft.department.startsWith("Select") ? undefined : formDraft.department,
          designation: formDraft.designation.trim(),
          date_of_joining: formDraft.joiningDate,
          manager: formDraft.manager.startsWith("Select") ? undefined : formDraft.manager,
          employment_type: formDraft.employmentType.startsWith("Select") ? undefined : formDraft.employmentType,
          work_location: formDraft.workLocation.startsWith("Select") ? undefined : formDraft.workLocation,
          role_ids: [roleId],
          access_level: selectedRole.level,
          funding_account_ids: selectedAccountIds,
          status: formDraft.status.toUpperCase(),
          address: hasContactAddress
            ? {
                line1: formDraft.address.trim(),
                city: formDraft.city.trim(),
                state: formDraft.state.startsWith("Select") ? undefined : formDraft.state,
                pin_code: formDraft.pinCode.trim(),
                country: formDraft.country.trim(),
                landmark: formDraft.landmark.trim(),
              }
            : undefined,
          id_proof_type: formDraft.idProofType.startsWith("Select") ? undefined : formDraft.idProofType,
          id_proof_number: formDraft.idProofNumber.trim(),
          blood_group: formDraft.bloodGroup.startsWith("Select") ? undefined : formDraft.bloodGroup,
          emergency_contact: formDraft.emergencyMobile
            ? {
                name: formDraft.emergencyName.trim(),
                phone_number: `${formDraft.emergencyCode}${digitsOnly(formDraft.emergencyMobile)}`,
                relationship: formDraft.relationship.trim(),
              }
            : undefined,
          require_2fa: true,
        });
        const result = isEditMode
          ? await payoutApi.updateUser(
              initialUser?.id || initialUser?.email || formDraft.companyEmail,
              body,
              stableIdempotencyKey("update-user", `${formDraft.companyEmail}-${selectedRole.level}-${selectedAccountIds.join("-")}`),
              stepUpToken,
            )
          : await payoutApi.createUser(
              body,
              stableIdempotencyKey("create-user", `${formDraft.companyEmail}-${selectedRole.level}-${selectedAccountIds.join("-")}`),
              stepUpToken,
            );
        writeSessionJson("payoutUserCreateResult", result);
        navigate("/payouts/users");
      } catch (error) {
        showSubmitError(
          errorMessage(
            error,
            isEditMode
              ? "Edit user verification: unable to update this user. Please try again."
              : "Add user verification: unable to create this user. Please try again.",
          ),
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setCurrentStep((step) => Math.min(5, step + 1));
  };

  const handleRoleChange = (level: string) => {
    const role = addUserRoles.find((item) => item.level === level);
    if (role) setSelectedRole(role);
  };

  const toggleAccount = (accountId: string) => {
    setSelectedAccountIds((current) =>
      current.includes(accountId)
        ? current.filter((id) => id !== accountId)
        : [...current, accountId],
    );
  };

  const toggleAccountGroup = (accountIds: string[]) => {
    setSelectedAccountIds((current) => {
      const groupSelected = accountIds.every((id) => current.includes(id));
      if (groupSelected) return current.filter((id) => !accountIds.includes(id));
      return Array.from(new Set([...current, ...accountIds]));
    });
  };

  const toggleSelectAllAccounts = () => {
    setSelectedAccountIds((current) =>
      current.length === liveAddUserAccounts.length
        ? []
        : liveAddUserAccounts.map((account) => account.id),
    );
  };

  const handleVerificationCodeChange = (index: number, value: string) => {
    const nextValue = value.replace(/\D/g, "").slice(-1);
    setVerificationCode((current) => {
      const next = [...current];
      next[index] = nextValue;
      return next;
    });

    if (nextValue && index < 5) {
      const nextInput = document.getElementById(`add-user-otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleVerificationKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Backspace" && !verificationCode[index] && index > 0) {
      const previousInput = document.getElementById(`add-user-otp-${index - 1}`);
      previousInput?.focus();
    }
  };

  const visibleAccountGroups = liveAddUserAccountOptions
    .map((group) => ({
      ...group,
      accounts: group.accounts.filter((account) => {
        const query = accountSearch.trim().toLowerCase();
        if (!query) return true;
        return (
          account.name.toLowerCase().includes(query) ||
          account.bank.toLowerCase().includes(query) ||
          account.accountNumber.toLowerCase().includes(query)
        );
      }),
    }))
    .filter((group) => group.accounts.length > 0);

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold leading-8 text-[#1B163B]">
            {isEditMode ? `Edit ${formDraft.fullName || initialUser?.name || "User"}` : "Add User"}
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            {isEditMode
              ? "Update user details, assigned role and accessible payout accounts."
              : "Invite a user, assign a predefined role and choose accessible accounts."}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/payouts/users"
            className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
          >
            Cancel
          </Link>
          <button
            type="button"
            onClick={handleContinue}
            disabled={
              submitting ||
              (currentStep === 5 && stepUpRequired && verificationCode.join("").length !== 6)
            }
            className="flex h-10 items-center gap-4 rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
          >
            {submitting
              ? isEditMode ? "Saving..." : "Creating..."
              : currentStep === 5
              ? isEditMode ? "Save Changes" : "Create User"
              : "Continue"}
            <FiArrowRight className="text-lg" />
          </button>
        </div>
      </div>
      <section className="mb-5 grid rounded-[8px] border border-[#E0E5F2] bg-white new-shadow md:grid-cols-5">
        <StepItem
          number={1}
          label="Personal Information"
          active={currentStep === 1}
          complete={currentStep > 1}
          onClick={() => setCurrentStep(1)}
        />
        <StepItem
          number={2}
          label="Assign Role"
          active={currentStep === 2}
          complete={currentStep > 2}
          onClick={() => setCurrentStep(2)}
        />
        <StepItem
          number={3}
          label="Assign Accounts"
          active={currentStep === 3}
          complete={currentStep > 3}
          onClick={() => setCurrentStep(3)}
        />
        <StepItem
          number={4}
          label="Review"
          active={currentStep === 4}
          complete={currentStep > 4}
          onClick={() => setCurrentStep(4)}
        />
        <StepItem number={5} label="Verification" active={currentStep === 5} />
      </section>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        {currentStep === 1 ? (
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <div className="mb-5">
            <h2 className="text-[16px] font-semibold text-[#1B163B]">
              1. Personal Information
            </h2>
            <p className="mt-2 text-xs font-medium text-[#505E8E]">
              Enter the personal details of the user.
            </p>
          </div>

          <div className="space-y-5">
            <div className="grid gap-5 lg:grid-cols-3">
              <TextInput label="Full Name" placeholder="Enter full name" required value={formDraft.fullName} onChange={updateDraft("fullName")} />
              <TextInput label="Employee ID" placeholder="Enter employee ID" value={formDraft.employeeId} onChange={updateDraft("employeeId")} />
              <DateInput label="Date of Birth" value={formDraft.dob} onChange={updateDraft("dob")} />
            </div>

            <div className="grid gap-5 lg:grid-cols-3">
              <SelectInput label="Gender" value={formDraft.gender} onChange={updateDraft("gender")} />
              {isEditMode ? (
                <TextInput
                  label="Company Email"
                  placeholder="Company email"
                  required
                  value={formDraft.companyEmail}
                  onChange={updateDraft("companyEmail")}
                />
              ) : (
                <MerchantEmailSelect
                  label="Company Email"
                  required
                  value={formDraft.companyEmail}
                  options={eligibleEmailOptions}
                  onSelect={handleSelectMerchantEmail}
                />
              )}
              <TextInput label="Personal Email" placeholder="Enter personal email" value={formDraft.personalEmail} onChange={updateDraft("personalEmail")} />
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
              <PhoneInput label="Mobile Number" placeholder="Enter mobile number" code={formDraft.mobileCode} value={formDraft.mobile} onCodeChange={updateDraft("mobileCode")} onChange={updateDraft("mobile")} />
              <PhoneInput label="Alternate Mobile Number" placeholder="Enter alternate mobile number" code={formDraft.alternateMobileCode} value={formDraft.alternateMobile} onCodeChange={updateDraft("alternateMobileCode")} onChange={updateDraft("alternateMobile")} />
            </div>

            <div>
              <h3 className="mb-4 text-sm font-semibold text-[#1B163B]">
                Employment Details
              </h3>
              <div className="grid gap-5 lg:grid-cols-4">
                <SelectInput label="Department" value={formDraft.department} onChange={updateDraft("department")} />
                <TextInput label="Designation" placeholder="Enter designation" value={formDraft.designation} onChange={updateDraft("designation")} />
                <DateInput label="Date of Joining" value={formDraft.joiningDate} onChange={updateDraft("joiningDate")} />
                <SelectInput
                  label="Manager (Reporting To)"
                  value={formDraft.manager}
                  onChange={updateDraft("manager")}
                  options={
                    managerNameOptions.length
                      ? managerNameOptions
                      : ["Select manager"]
                  }
                />
                <SelectInput
                  label="Employment Type"
                  value={formDraft.employmentType}
                  onChange={updateDraft("employmentType")}
                  className="lg:col-span-2"
                />
                <SelectInput label="Work Location" value={formDraft.workLocation} onChange={updateDraft("workLocation")} />
                <SelectInput label="User Status" value={formDraft.status} onChange={updateDraft("status")} />
              </div>
            </div>

            <div>
              <h3 className="mb-4 text-sm font-semibold text-[#1B163B]">
                Contact & Address
              </h3>
              <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
                <TextAreaInput label="Home Address" placeholder="Enter complete address" value={formDraft.address} onChange={updateDraft("address")} />
                <TextInput label="City" placeholder="Enter city" value={formDraft.city} onChange={updateDraft("city")} />
                <SelectInput label="State" value={formDraft.state} onChange={updateDraft("state")} />
                <TextInput label="PIN Code" placeholder="Enter PIN code" value={formDraft.pinCode} onChange={updateDraft("pinCode")} />
                <SelectInput label="Country" value={formDraft.country} onChange={updateDraft("country")} />
                <div className="lg:col-span-2">
                  <TextInput label="Landmark (Optional)" placeholder="Enter landmark" value={formDraft.landmark} onChange={updateDraft("landmark")} />
                </div>
              </div>
            </div>

            <div>
              <h3 className="mb-4 text-sm font-semibold text-[#1B163B]">
                Additional Information
              </h3>
              <div className="grid gap-5 lg:grid-cols-4">
                <SelectInput label="ID Proof Type" value={formDraft.idProofType} onChange={updateDraft("idProofType")} />
                <TextInput label="ID Proof Number" placeholder="Enter ID number" value={formDraft.idProofNumber} onChange={updateDraft("idProofNumber")} />
                <SelectInput label="Blood Group (Optional)" value={formDraft.bloodGroup} onChange={updateDraft("bloodGroup")} />
                <TextInput label="Emergency Contact Name" placeholder="Enter contact name" value={formDraft.emergencyName} onChange={updateDraft("emergencyName")} />
                <PhoneInput
                  label="Emergency Contact Number"
                  placeholder="Enter emergency number"
                  className="lg:col-span-2"
                  code={formDraft.emergencyCode}
                  value={formDraft.emergencyMobile}
                  onCodeChange={updateDraft("emergencyCode")}
                  onChange={updateDraft("emergencyMobile")}
                />
                <TextInput label="Relationship" placeholder="Enter relationship" value={formDraft.relationship} onChange={updateDraft("relationship")} />
              </div>
            </div>
          </div>
        </section>
        ) : currentStep === 2 ? (
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <div className="mb-6">
            <h2 className="text-[16px] font-semibold text-[#1B163B]">
              2. Assign Role
            </h2>
            <p className="mt-2 text-xs font-medium text-[#505E8E]">
              Choose a predefined role for the user. Each role comes with a set of permissions.
            </p>
          </div>

          <div className="grid gap-5 lg:grid-cols-[390px_minmax(0,1fr)]">
            <div>
              <SelectInput
                label="Role"
                value={selectedRole.level}
                required
                options={addUserRoles.map((role) => role.level)}
                onChange={handleRoleChange}
              />

              <div className="mt-4 overflow-hidden rounded-[8px] border border-[#E0E5F2] bg-white shadow-[0_16px_28px_rgba(27,22,59,0.08)]">
                <div className="space-y-3 p-4">
                  {addUserRoles.map((role) => {
                    const isSelected = role.level === selectedRole.level;
                    return (
                      <button
                        key={role.level}
                        type="button"
                        onClick={() => handleRoleChange(role.level)}
                        className={
                          "flex min-h-[78px] w-full items-center gap-4 rounded-[6px] border px-4 text-left transition-colors " +
                          (isSelected
                            ? "border-[#1B163B] bg-[#A9B2CF1A]"
                            : "border-[#E0E5F2] bg-white hover:bg-[#F8FAFD]")
                        }
                      >
                        <span
                          className={
                            "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border " +
                            (isSelected ? "border-[#1B163B]" : "border-[#A3AED0]")
                          }
                        >
                          {isSelected && <span className="h-2 w-2 rounded-full bg-[#1B163B]" />}
                        </span>
                        <span className={"flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg " + role.tone}>
                          {role.icon}
                        </span>
                        <span>
                          <span className="block text-sm font-semibold text-[#1B163B]">
                            {role.level}
                          </span>
                          <span className="mt-1 block text-xs font-medium leading-5 text-[#505E8E]">
                            {role.description}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="rounded-[8px] border border-[#E0E5F2] bg-white p-6">
              <span className="inline-flex rounded-[5px] bg-[#A9B2CF1A] px-3 py-1 text-xs font-semibold text-[#6687FF]">
                {selectedRole.level}
              </span>
              <h3 className="mt-5 text-[20px] font-semibold text-[#6687FF]">
                {selectedRole.title}
              </h3>
              <p className="mt-4 max-w-xl text-sm font-medium leading-6 text-[#505E8E]">
                Users with this role can perform day-to-day payout operations based on the selected access level and inherited permissions.
              </p>

              <div className="mt-7">
                <p className="mb-3 text-sm font-semibold text-[#1B163B]">
                  This role can:
                </p>
                <div className="space-y-3">
                  {selectedRole.can.map((item) => (
                    <div key={item} className="flex items-center gap-3 text-sm font-medium text-[#1B163B]">
                      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#16A34A1A] text-[#16A34A]">
                        <FiCheckCircle className="text-xs" />
                      </span>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-7 rounded-[8px] border border-[#F59E0B66] bg-[#F59E0B12] p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#92400E]">
                  <FiLock /> This role cannot:
                </div>
                <div className="space-y-3">
                  {selectedRole.cannot.map((item) => (
                    <div key={item} className="flex items-center gap-3 text-sm font-medium text-[#92400E]">
                      <FiX className="text-[#EF4444]" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
        ) : currentStep === 3 ? (
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <div className="mb-5">
            <h2 className="text-[16px] font-semibold text-[#1B163B]">
              3. Assign Accounts
            </h2>
            <p className="mt-2 text-xs font-medium text-[#505E8E]">
              Select the bank accounts and entities this user can access. Access is controlled by the selected role and its permissions.
            </p>
          </div>

          <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex h-10 min-w-[280px] max-w-[420px] flex-1 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-3">
              <input
                value={accountSearch}
                onChange={(event) => setAccountSearch(event.target.value)}
                className="w-full bg-transparent text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
                placeholder="Search accounts by name or number"
              />
              <FiSearch className="ml-2 shrink-0 text-[#505E8E]" />
            </div>
            <div className="flex items-center gap-5 text-xs font-semibold">
              <span className="text-[#505E8E]">
                {selectedAccountIds.length} accounts selected
              </span>
              <button
                type="button"
                onClick={toggleSelectAllAccounts}
                className="text-[#6687FF]"
              >
                {selectedAccountIds.length === liveAddUserAccounts.length ? "Clear All" : "Select All"}
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {visibleAccountGroups.map((group) => (
              <AccountGroup
                key={group.group}
                group={group}
                selectedAccountIds={selectedAccountIds}
                onToggleAccount={toggleAccount}
                onToggleGroup={toggleAccountGroup}
              />
            ))}
          </div>
        </section>
        ) : currentStep === 4 ? (
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <div className="mb-5">
            <h2 className="text-[16px] font-semibold text-[#1B163B]">
              4. Review
            </h2>
            <p className="mt-2 text-xs font-medium text-[#505E8E]">
              Review the details before {isEditMode ? "saving changes" : "creating the user"}.
            </p>
          </div>

          <div className="space-y-5">
            <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-4">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[#1B163B]">
                  Personal Information
                </h3>
                <ReviewEditButton onClick={() => setCurrentStep(1)} />
              </div>
              <div className="grid gap-x-12 gap-y-4 text-xs font-medium text-[#505E8E] md:grid-cols-2">
                {[
                  ["Full Name", formDraft.fullName || "-"],
                  ["Mobile Number", formDraft.mobile ? `${formDraft.mobileCode} ${formDraft.mobile}` : "-"],
                  ["Employee ID", formDraft.employeeId || "-"],
                  ["Alternate Mobile Number", formDraft.alternateMobile ? `${formDraft.alternateMobileCode} ${formDraft.alternateMobile}` : "-"],
                  ["Date of Birth", formDraft.dob || "-"],
                  ["Home Address", [formDraft.address, formDraft.city, formDraft.state, formDraft.pinCode].filter(Boolean).join(", ") || "-"],
                  ["Gender", formDraft.gender.startsWith("Select") ? "-" : formDraft.gender],
                  ["Date of Joining", formDraft.joiningDate || "-"],
                  ["Company Email", formDraft.companyEmail || "-"],
                  ["Manager (Reporting To)", formDraft.manager.startsWith("Select") ? "-" : formDraft.manager],
                  ["Personal Email", formDraft.personalEmail || "-"],
                ].map(([label, value]) => (
                  <div key={label}>
                    <span className="block">{label}</span>
                    <span className="mt-1 block font-semibold text-[#1B163B]">
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-4">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[#1B163B]">
                  Role Information
                </h3>
                <ReviewEditButton onClick={() => setCurrentStep(2)} />
              </div>
              <div className="grid gap-4 text-xs font-medium text-[#505E8E] md:grid-cols-2">
                <div>
                  <span className="block">Role</span>
                  <span className="mt-1 block font-semibold text-[#1B163B]">
                    {selectedRole.level}
                  </span>
                </div>
                <div>
                  <span className="block">Access Level</span>
                  <span className="mt-1 block font-semibold text-[#1B163B]">
                    {selectedRole.level}
                  </span>
                </div>
              </div>
            </section>

            <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-4">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[#1B163B]">
                  Assigned Accounts ({selectedAccounts.length})
                </h3>
                <ReviewEditButton onClick={() => setCurrentStep(3)} />
              </div>
              <div className="overflow-hidden rounded-[8px] border border-[#E0E5F2]">
                <div className="grid grid-cols-[minmax(180px,1.2fr)_minmax(160px,1fr)_minmax(130px,0.8fr)_100px_80px] bg-[#F0F3F8] px-4 py-3 text-xs font-semibold text-[#505E8E]">
                  <span>Account Name</span>
                  <span>Account Number</span>
                  <span>Account Type</span>
                  <span>Status</span>
                  <span>Currency</span>
                </div>
                {selectedAccounts.map((account) => (
                  <div
                    key={account.id}
                    className="grid grid-cols-[minmax(180px,1.2fr)_minmax(160px,1fr)_minmax(130px,0.8fr)_100px_80px] items-center border-t border-[#E0E5F2] px-4 py-4 text-xs font-medium text-[#1B163B]"
                  >
                    <span className="flex items-center gap-3">
                      <img
                        src={account.logoUrl}
                        alt={`${account.bank} logo`}
                        className="h-6 w-16 object-contain"
                      />
                    </span>
                    <span>{account.bank} **** {account.accountNumber}</span>
                    <span>{account.type}</span>
                    <AccountStatusBadge />
                    <span>{account.currency}</span>
                  </div>
                ))}
              </div>
            </section>

            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
              <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-4">
                <h3 className="mb-4 text-sm font-semibold text-[#1B163B]">
                  What this user will be able to do?
                </h3>
                <div className="grid gap-3 text-xs font-medium text-[#1B163B] sm:grid-cols-2">
                  {selectedRole.can.concat(selectedRole.cannot.slice(0, 3).map((item) => `Cannot ${item.toLowerCase()}`)).map((item) => (
                    <span key={item} className="flex items-center gap-2">
                      <FiCheckCircle className="shrink-0 text-[#16A34A]" />
                      {item}
                    </span>
                  ))}
                </div>
              </section>

              <InfoCallout icon={<FiInfo />}>
                <span>
                  <span className="mb-2 block font-semibold text-[#1B163B]">
                    Important
                  </span>
                  {isEditMode
                    ? "Changes to user details, role and account access will be applied after verification."
                    : "The user will receive an invite on their company email address with instructions to set their password."}
                </span>
              </InfoCallout>
            </div>
          </div>
        </section>
        ) : (
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <div className="mb-6">
            <h2 className="text-[16px] font-semibold text-[#1B163B]">
              5. Verification
            </h2>
            <p className="mt-2 text-xs font-medium text-[#505E8E]">
              Verify this user {isEditMode ? "update" : "invitation"} before {isEditMode ? "saving changes" : "creating access in your organization"}.
            </p>
          </div>

          <div className="rounded-[8px] border border-[#E0E5F2] bg-white p-6">
            <div className="mb-6 flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-[#A9B2CF1A] text-xl text-[#1B163B]">
                <FiShield />
              </span>
              <div>
                <h3 className="text-[18px] font-semibold text-[#1B163B]">
                  {isEditMode ? "Verify User Update" : "Verify User Creation"}
                </h3>
                <p className="mt-2 text-sm font-medium text-[#505E8E]">
                  Enter the 6-digit code from the configured Google Authenticator app.
                </p>
              </div>
            </div>

            <div>
              <div className="flex min-h-[86px] items-center justify-between rounded-[8px] border border-[#1B163B] bg-white px-5 text-left">
                <span className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-[8px] bg-[#16A34A1A] text-[#16A34A]">
                    <FiShield />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-[#1B163B]">
                      Google Authenticator
                    </span>
                    <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                      Recommended for role and account access changes
                    </span>
                  </span>
                </span>
                <FiCheck className="text-[#16A34A]" />
              </div>
            </div>

            <div className="mt-8">
              <FieldLabel required>Verification Code</FieldLabel>
              <div className="flex flex-wrap gap-3">
                {verificationCode.map((digit, index) => (
                  <input
                    key={index}
                    id={`add-user-otp-${index}`}
                    value={digit}
                    inputMode="numeric"
                    maxLength={1}
                    onChange={(event) =>
                      handleVerificationCodeChange(index, event.target.value)
                    }
                    onKeyDown={(event) => handleVerificationKeyDown(index, event)}
                    className="h-14 w-14 rounded-[8px] border border-[#E0E5F2] bg-white text-center text-xl font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
                  />
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-6">
              <button
                type="button"
                onClick={() => setVerificationCode(Array(6).fill(""))}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#1B163B]"
              >
                <FiRefreshCw /> Resend code
              </button>
            </div>

            <InfoCallout icon={<FiInfo />}>
              This verification protects user access, payout permissions and assigned bank accounts.
            </InfoCallout>
          </div>
        </section>
        )}

        <UserSummary
          selectedRole={currentStep >= 2 ? selectedRole : undefined}
          selectedAccountCount={selectedAccountIds.length}
          showAccountCount={currentStep >= 3}
          name={formDraft.fullName || (isEditMode ? "User" : "New User")}
          initials={
            formDraft.fullName
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part[0]?.toUpperCase())
              .join("") || (isEditMode ? initialUser?.initials || "-" : "-")
          }
        />
      </div>

    </div>
  );
}

export default PayoutAddUser;
